/*
  Product gallery toggle: Product images <-> Nutrition Facts.

  Every image lives in one product gallery. Liquid stamps each media item with
  `data-kind` (product|nutrition) and `data-variant` (a handleized option token, or
  "all" for shared images), both parsed from the FILENAME. This element hides and
  shows them; nothing is duplicated and no extra variants exist.

  The filter is deliberately STATELESS. Dawn re-renders the gallery on every variant
  change (product-info.js `updateMedia`, which also reorders items via `prepend`), so
  any class or style JS put on a node can vanish. Every pass recomputes all items from
  `(mode, variantToken)` rather than tracking what was hidden before.

  `variantChange` publishes AFTER updateMedia finishes, so one listener is enough.
*/
if (!customElements.get('gallery-filter')) {
  customElements.define(
    'gallery-filter',
    class GalleryFilter extends HTMLElement {
      connectedCallback() {
        this.gallery = document.getElementById(this.dataset.gallery);
        if (!this.gallery) return;

        this.tokens = this.safeParse(this.dataset.variantTokens);
        this.mode = this.dataset.defaultMode || 'all';
        // Deliberately null on load: the whole gallery is shown until the shopper picks a
        // flavour themselves. Dawn preselects a variant, but that is not a choice, and
        // narrowing to it immediately would hide the other flavours nobody asked to hide.
        // A variant is identified by ALL of its option values plus their joined
        // combination, so a panel named for one option (e.g. `__fruit-punch__`, shared by
        // the 30- and 50-serving tubs) matches, and one named for the exact combination
        // (`__30-servings-fruit-punch__`) still wins by being more specific.
        this.variantToken = null;

        this.buttons = Array.from(this.querySelectorAll('[data-kind-toggle]'));
        this.buttons.forEach((b) =>
          b.addEventListener('click', () => this.setMode(b.dataset.kindToggle))
        );

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.dataset.section) return;
          const variant = event.data.variant;
          this.variantToken = variant ? this.tokens[variant.id] || null : null;
          this.apply();
        });

        this.apply();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      safeParse(raw) {
        try {
          return JSON.parse(raw || '{}');
        } catch (e) {
          return {};
        }
      }

      setMode(mode) {
        if (mode === this.mode) return;
        this.mode = mode;
        this.apply();
      }

      /* Does one gallery item belong in the current view? */
      matches(el, useVariant) {
        const kind = el.dataset.kind;
        // Untagged media (anything not following the naming convention) always shows,
        // so a missing or misnamed file degrades to "visible", never to a blank gallery.
        if (!kind) return this.mode !== 'nutrition'; // TMS: videos and untagged shots stay out of the nutrition view
        // 'all' accepts both kinds; it still narrows by variant once one is chosen.
        if (this.mode !== 'all' && kind !== this.mode) return false;
        if (!useVariant || !this.variantToken || !this.variantToken.length) return true;
        const v = el.dataset.variant;
        return !v || v === 'all' || this.variantToken.indexOf(v) !== -1;
      }

      apply() {
        const slides = Array.from(this.gallery.querySelectorAll('.product__media-item'));
        const thumbs = Array.from(this.gallery.querySelectorAll('.thumbnail-list__item'));
        if (!slides.length) return;

        // Narrow by variant first; if that empties the gallery (e.g. this flavour has no
        // panel yet), fall back to showing every item of the chosen kind.
        let useVariant = slides.some((el) => this.matches(el, true));
        if (!slides.some((el) => this.matches(el, useVariant))) useVariant = false;

        // If the chosen kind matches nothing at all, show everything rather than nothing.
        const anyMatch = slides.some((el) => this.matches(el, useVariant));

        [slides, thumbs].forEach((group) =>
          group.forEach((el) => {
            const show = anyMatch ? this.matches(el, useVariant) : true;
            el.classList.toggle('gallery-filtered-out', !show);
          })
        );

        this.buttons.forEach((b) =>
          b.setAttribute('aria-pressed', String(b.dataset.kindToggle === this.mode))
        );

        this.refreshSliders();
        this.promoteFirstVisible(slides);
      }

      /* Dawn's SliderComponent counts only items with clientWidth > 0, so hidden slides
         drop out of its paging by themselves - it just needs to recount. */
      refreshSliders() {
        this.gallery.querySelectorAll('slider-component').forEach((s) => {
          if (typeof s.resetPages === 'function') s.resetPages();
          // TMS: Dawn skips its recount below 2 visible slides, leaving e.g. "1/4". Patch it.
          const shown = Array.from(s.querySelectorAll('.product__media-item')).filter((el) => el.clientWidth > 0).length;
          const cur = s.querySelector('.slider-counter--current');
          const tot = s.querySelector('.slider-counter--total');
          if (shown < 2 && tot) {
            if (cur) cur.textContent = shown;
            tot.textContent = shown;
          }
          s.querySelectorAll('.slider-button').forEach((b) => (b.style.visibility = shown < 2 ? 'hidden' : ''));
        });
      }

      promoteFirstVisible(slides) {
        const active = slides.find(
          (el) => el.classList.contains('is-active') && !el.classList.contains('gallery-filtered-out')
        );
        if (active) return;
        const first = slides.find((el) => !el.classList.contains('gallery-filtered-out'));
        if (first && typeof this.gallery.setActiveMedia === 'function') {
          this.gallery.setActiveMedia(first.dataset.mediaId, false);
        }
      }
    }
  );
}
